/**
 * Every command payload, as a Zod schema.
 *
 * Naming conventions, held to across the whole set:
 *
 * - `recipe.*` addresses one recipe and always takes `recipeId` first.
 * - `water.*` addresses one recipe's water section.
 * - `equipment.*` / `waterProfile.*` / `settings.*` are workspace-scoped.
 *   `waterProfile.*` is deliberately a *different domain* from `water.*`: one
 *   manages the workspace's library of saved analyses, the other edits the copy
 *   inside one recipe.
 * - list operations are always the same four shapes — `add…` (item + optional
 *   index), `update…` (`itemId` + a partial `changes`), `remove…` (`itemId`)
 *   and `reorder…` (the complete new order as ids). Four verbs × four lists
 *   means the UI's grid component and the assistant's tool schema each learn
 *   one pattern, not sixteen.
 *
 * Payload fields may be *absent* (`.optional()`) where a caller reasonably
 * omits them; persisted document fields never are (`T | null`, always present —
 * see `document/common.ts`). The asymmetry is deliberate: payloads are
 * hand-written by humans and by the assistant, documents are machine-written
 * and must round-trip through JSON unchanged.
 */

import { z } from 'zod';
import {
  batchCellarTaskSchema,
  batchCostSnapshotSchema,
  batchDocSchema,
  batchFermentationReadingSchema,
  batchGravityVolumeSchema,
  batchKettleMeasurementSchema,
  batchPhotoSchema,
  batchPitchedYeastSchema,
  batchStatusSchema,
  batchTastingSchema,
  measuredVolumeSchema,
  specificGravitySchema,
} from '../document/batch';
import {
  blendDocFieldsSchema,
  blendDocSchema,
  blendStageSchema,
  isBlendDatedStage,
  liftBlendAdditions,
} from '../document/blend';
import {
  acidChoiceIdSchema,
  batchVolumeBasisSchema,
  currencyCodeSchema,
  documentIdSchema,
  hopPackagingSchema,
  hopStoragePlaceSchema,
  isoDateSchema,
  nameSchema,
  nonNegativeSchema,
  notesSchema,
  packagingMethodSchema,
  percentSchema,
  positiveSchema,
  primingSugarSchema,
  saltIdSchema,
  tagsSchema,
  timestampSchema,
} from '../document/common';
import { customIngredientDocSchema } from '../document/custom-ingredient';
import {
  BREW_REMINDERS_FIELD,
  DECOCTION_FIELDS,
  equipmentProfileDocFieldsSchema,
  equipmentProfileDocSchema,
  FERMENTER_VOLUME_FIELD,
} from '../document/equipment';
import {
  FERMENTATION_STEP_PRESSURE_FIELD,
  MASH_STEP_DECOCTION_FIELD,
  RECIPE_YEAST_STARTER_FIELD,
  recipeAcidAdditionSchema,
  recipeFermentableFieldsSchema,
  recipeFermentableSchema,
  recipeFermentationStepSchema,
  recipeHopFieldsSchema,
  recipeHopSchema,
  recipeMashStepSchema,
  recipeMiscFieldsSchema,
  recipeMiscSchema,
  recipeSaltAdditionSchema,
  recipeYeastFieldsSchema,
  recipeYeastSchema,
} from '../document/ingredients';
import {
  cropYearSchema,
  inventoryItemDocSchema,
  inventoryItemFieldsSchema,
  yeastHarvestSchema,
} from '../document/inventory';
import { journalBlockSchema, journalDocSchema } from '../document/journal';
import { miscProfileChangesSchema, miscProfileDocSchema } from '../document/misc-profile';
import { recipeDocSchema } from '../document/recipe';
import { recipeSnapshotDocSchema } from '../document/recipe-snapshot';
import {
  fermentationProfileChangesSchema,
  fermentationProfileDocSchema,
  mashProfileChangesSchema,
  mashProfileDocSchema,
} from '../document/schedule-profile';
import { waterProfileDocSchema, waterRuleDocSchema } from '../document/water';
import { savedWaterProfileDocSchema } from '../document/water-profile';
import {
  brewAlertsSchema,
  massFormatSchema,
  starterLeadDaysSchema,
  unitPreferencesSchema,
  unitSystemSchema,
} from '../document/workspace';

/** Where in a list an inserted item lands; omitted means "append". */
const insertIndexSchema = z.number().int().min(0).max(1024).optional();

/** Addresses a recipe. Every `recipe.*` and `water.*` payload extends this. */
const recipeTarget = { recipeId: documentIdSchema };

// ---------------------------------------------------------------------------
// Recipe lifecycle
// ---------------------------------------------------------------------------

/**
 * Create a recipe, blank or from a supplied document.
 *
 * `from` is the duplicate/import path: the document is taken as a *template*,
 * its `id` replaced by `recipeId` and both timestamps set from the envelope, so
 * duplicating is "the same doc, new identity, created now". Restoring a deleted
 * recipe verbatim is a different operation — see `recipe.restore`.
 */
export const recipeCreatePayloadSchema = z.strictObject({
  /** Id for the new recipe. Must not already exist. */
  recipeId: documentIdSchema,
  /** Name for the new recipe; defaults to the template's name, or `Untitled recipe`. */
  name: nameSchema.optional(),
  /** A complete recipe to clone. Its `id`, `createdAt` and `updatedAt` are replaced. */
  from: recipeDocSchema.optional(),
});

/**
 * Insert a recipe document verbatim, timestamps and all.
 *
 * The exact inverse of `recipe.delete`, and the shape a full-state sync push
 * uses. Unlike `recipe.create` it preserves `createdAt`/`updatedAt`, which is
 * what makes undo of a delete a true identity.
 */
export const recipeRestorePayloadSchema = z.strictObject({
  /** The document to insert. Its own `id` is used. */
  recipe: recipeDocSchema,
});

/** Delete a recipe. */
export const recipeDeletePayloadSchema = z.strictObject({ ...recipeTarget });

/** Rename a recipe. */
export const recipeRenamePayloadSchema = z.strictObject({ ...recipeTarget, name: nameSchema });

/** Set (or clear, with `null`) the BJCP style the recipe is designed to. */
export const recipeSetStylePayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Catalog style id such as `bjcp-2021-21a`, or `null`. */
  styleId: z.string().min(1).max(80).nullable(),
});

/**
 * Put a recipe away, or bring it back.
 *
 * A boolean, not the date. The reducer has a deterministic clock in
 * `command.timestamp`, and a payload carrying a wall clock would replay to a
 * different document on every machine that read the log.
 */
export const recipeSetArchivedPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** `true` puts it away, `false` brings it back to the list. */
  archived: z.boolean(),
});

/** Set the target batch volume, and optionally which point in the waterfall it measures. */
export const recipeSetBatchSizePayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Target batch volume, **litres**. */
  batchVolumeL: positiveSchema.max(10_000),
  /** What the volume measures; unchanged when omitted. */
  batchVolumeBasis: batchVolumeBasisSchema.optional(),
});

/** Set the boil length. */
export const recipeSetBoilTimePayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Boil duration, **minutes**. */
  boilTimeMinutes: z.number().finite().min(0).max(1440),
});

/** Point the recipe at an equipment profile, or at the workspace default with `null`. */
export const recipeSetEquipmentPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Profile id, or `null` for the workspace default. Must exist when non-null. */
  equipmentProfileId: documentIdSchema.nullable(),
});

/**
 * Override the profile's mash efficiency for this recipe, or clear it with
 * `null`.
 *
 * Bounded by `percentSchema` exactly as the profile's own figure is, so an
 * override can say anything a profile could say and nothing it could not.
 */
export const recipeSetEfficiencyPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Mash efficiency, **%**, or `null` to follow the equipment profile. */
  mashEfficiencyPercentOverride: percentSchema.nullable(),
});

/**
 * State the OG this recipe is aiming at, or clear it with `null`.
 *
 * Bounded by the same `specificGravitySchema` a measured gravity is, because a
 * target and a measurement are the same physical quantity. `null` clears back
 * to the style's own band rather than to a number, which is the whole point of
 * the field being nullable — see `document/recipe.ts`.
 */
export const recipeSetTargetOgPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Target OG, **SG**, or `null` to follow the recipe's style. */
  targetOg: specificGravitySchema.nullable(),
});

/**
 * State the colour this recipe is aiming at, or clear it with `null`.
 *
 * SRM, and bounded exactly as the document's own field is, because a payload
 * that could carry a colour the document cannot hold would be a command that
 * fails at the reducer rather than at the parser. `null` clears back to the
 * style's band — see `document/recipe.ts`.
 */
export const recipeSetTargetSrmPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Target colour, **SRM**, or `null` to follow the recipe's style. */
  targetSrm: nonNegativeSchema.max(200).nullable(),
});

/** Set the target carbonation. */
export const recipeSetCarbonationPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Target carbonation, **volumes of CO₂**. */
  carbonationVolumes: z.number().finite().min(0).max(6),
});

/**
 * Set how the beer is packaged and what it is primed with.
 *
 * Both fields are `.optional()` rather than defaulted, so setting the method
 * alone cannot quietly reset the sugar. One command rather than two because the
 * packaging panel is where both are chosen, and a brewer switching to kegging
 * and picking table sugar should undo as one step rather than two.
 */
export const recipeSetPackagingPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** How the beer leaves the fermenter; unchanged when omitted. */
  packagingMethod: packagingMethodSchema.optional(),
  /** What a conditioning package is primed with; unchanged when omitted. */
  primingSugar: primingSugarSchema.optional(),
});

/** Replace the recipe's notes. */
export const recipeSetNotesPayloadSchema = z.strictObject({
  ...recipeTarget,
  notes: notesSchema,
});

/** Replace the recipe's tags. */
export const recipeSetTagsPayloadSchema = z.strictObject({ ...recipeTarget, tags: tagsSchema });

/**
 * A brewhouse efficiency figure, **%**.
 *
 * Bounded like every other efficiency the app stores (`percentSchema`) except
 * that zero is excluded: `recipe.scale` divides by the target, and a rig with
 * 0 % efficiency produces no beer to rescale onto.
 */
const efficiencyPercentSchema = z.number().finite().gt(0).max(100);

/**
 * Scale a recipe by a factor, and/or from one brewhouse efficiency to another.
 *
 * Two independent axes in **one** command, because one press of *Scale* is one
 * edit and must be one ⌘Z. They compose in the obvious order — size first, then
 * efficiency — and the inverse is unaffected: it was already a full
 * `recipe.restore` (see the definition), which is exact for any number of
 * composed multiplications.
 *
 * **What scales** (extensive quantities — they are amounts of stuff):
 * batch volume; every fermentable, hop and misc amount; salt grams; acid
 * millilitres and grams; each mash step's explicit `infusionVolumeL`.
 *
 * **What does not** (intensive quantities — they are ratios, rates or
 * settings): boil time; all temperatures and step durations; alpha acid and
 * every other percentage; mash thickness (L/kg); carbonation volumes; target
 * pH; dilution fraction; water ion concentrations (ppm is already per-litre);
 * yeast `packs`; and the equipment profile, which describes the brewer's
 * system, not the recipe (see the README on why scale-invariance tests must
 * scale the profile's fixed losses separately).
 *
 * Yeast is the deliberate omission: rounding a pitch from 1 pack to 2.4 packs
 * is not a scaling operation, it is a pitch-rate calculation (PLAN §5.6), and
 * silently editing the number would be wrong in both directions.
 */
export const recipeScalePayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Multiplier. `2` doubles the batch, `0.5` halves it. `1` scales nothing. */
  factor: positiveSchema.max(1000),
  /**
   * Move the grist from the efficiency the recipe was written for onto the one
   * the brewer's rig actually runs at. Omitted means "leave the grist alone".
   *
   * Only additions whose efficiency depends on the equipment profile move —
   * mashed grain, and nothing else. Sugars and extracts dissolve completely on
   * any rig, so touching them would change the beer rather than preserve it.
   * See `document/scale.ts`'s `rescaleEfficiencyDoc` for the invariant and the
   * residuals, and note that both figures must be the same *kind* of efficiency
   * (the UI asks for brewhouse on both sides).
   */
  efficiency: z
    .strictObject({
      /** What the recipe's amounts were computed at, **%**. */
      fromPercent: efficiencyPercentSchema,
      /** What they are being moved onto, **%**. */
      toPercent: efficiencyPercentSchema,
    })
    .optional(),
});

/**
 * Set a recipe back to a historical state — the write half of version history.
 *
 * **A forward command, never a rewrite.** Rolling back is an edit like any
 * other: the log is append-only (it *is* the sync and dedupe mechanism, PLAN
 * §8.3), so a restore appends one more command carrying the old document rather
 * than truncating anything. Undoing a rollback is therefore ordinary undo.
 *
 * Distinct from `recipe.restore`, which is a verbatim upsert used for undo and
 * for whole-document sync deltas. This one keeps the living recipe's identity —
 * its id and its `createdAt` — and stamps `updatedAt` from the envelope, because
 * the recipe was not re-created, it was edited back to how it used to be.
 */
export const recipeRevertToPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** The historical document, from a named snapshot or a timeline version. */
  recipe: recipeDocSchema,
  /**
   * What the user picked, for the summary line — `Brewed as v3`, `15 Aug 2026
   * 14:03`. Supplied by the caller for the same reason a batch's name is: this
   * package has no clock and no locale.
   */
  label: z.string().min(1).max(120),
});

// ---------------------------------------------------------------------------
// Named recipe versions (snapshots)
// ---------------------------------------------------------------------------

/** Addresses one named snapshot. */
const snapshotTarget = { snapshotId: documentIdSchema };

/**
 * Create a named version from a fully-built document.
 *
 * `from` is **required**, exactly as `batch.create`'s is and for the same
 * reason: a snapshot *is* the recipe document it freezes, and this package can
 * neither read `state.recipes` on the caller's behalf nor name a version after
 * today's date. The caller builds it with {@link buildRecipeSnapshotDoc}.
 */
export const recipeSnapshotCreatePayloadSchema = z.strictObject({
  /** Id for the new snapshot. Must not already exist. */
  ...snapshotTarget,
  /** The complete snapshot document. Its `id`, `schemaVersion` and times are replaced. */
  from: recipeSnapshotDocSchema,
  /** Name for the new snapshot; defaults to the template's. */
  name: nameSchema.optional(),
});

/**
 * Insert a snapshot verbatim, timestamps and all — an **upsert**.
 *
 * The same duty `recipe.restore` and `batch.restore` carry: undo of
 * `recipeSnapshot.delete`, and the whole-document delta a full-state sync push
 * uses.
 */
export const recipeSnapshotRestorePayloadSchema = z.strictObject({
  /** The document to insert. Its own `id` is used. */
  snapshot: recipeSnapshotDocSchema,
});

/** Delete a named version. */
export const recipeSnapshotDeletePayloadSchema = z.strictObject({ ...snapshotTarget });

/** Rename a named version. */
export const recipeSnapshotRenamePayloadSchema = z.strictObject({
  ...snapshotTarget,
  name: nameSchema,
});

// ---------------------------------------------------------------------------
// Shared recipes (PLAN-COLLAB A2)
// ---------------------------------------------------------------------------

/**
 * Move a recipe of this workspace into a room: the recipe and its named
 * versions leave `recipes` and `recipeSnapshots`, and an `owner` reference
 * takes their place. **Server-issued**: the Worker sends it once the room holds
 * the recipe, so the workspace never drops a recipe the room does not have.
 */
export const recipeSharePayloadSchema = z.strictObject({
  ...recipeTarget,
  /** The owner's own equipment for it, or `null` for the room's shared default. */
  equipmentOverrideId: documentIdSchema.nullable(),
  /** When the room was made, **ms since epoch**. */
  joinedAt: timestampSchema,
});

/** Add an `editor` reference to a room this workspace was let into. **Server-issued.** */
export const sharedRecipeJoinPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** When the invite was accepted, **ms since epoch**. */
  joinedAt: timestampSchema,
});

/**
 * Drop a reference, leaving of one's own accord or removed. **Server-issued.**
 *
 * `copy` is the room's recipe as it stood at that moment (D-CO7), under a new
 * id so a later re-invite never collides with it; `null` when there is nothing
 * to keep, which is the owner deleting a recipe for everyone from their own
 * workspace (D-CO8a).
 */
export const sharedRecipeLeavePayloadSchema = z.strictObject({
  ...recipeTarget,
  copy: z
    .strictObject({
      /** Id for the private copy. Must not already exist here. */
      newRecipeId: documentIdSchema,
      /** The room's document. Its `id` is replaced by `newRecipeId`. */
      recipe: recipeDocSchema,
    })
    .nullable(),
});

/** Choose my own equipment for a shared recipe, or `null` to follow the shared default. */
export const sharedRecipeSetEquipmentOverridePayloadSchema = z.strictObject({
  ...recipeTarget,
  equipmentProfileId: documentIdSchema.nullable(),
});

// ---------------------------------------------------------------------------
// Ingredient lists
// ---------------------------------------------------------------------------

/**
 * Fields of a fermentable an update may change (everything but its id).
 *
 * Derived from the *Fields* schema, not the refined document: Zod refuses
 * `.omit()` on a refined object. The one-provenance rule is not lost by that —
 * the update reducer re-checks it on the merged row, where it belongs, because
 * a patch that sets `ingredientId` on a catalog-backed row is only wrong in
 * combination with what the row already holds.
 */
export const recipeFermentableChangesSchema = recipeFermentableFieldsSchema
  .omit({ itemId: true })
  .partial();

/**
 * Fields of a hop an update may change. @see recipeFermentableChangesSchema
 *
 * `whirlpoolEndTempC` is restated without `.default(null)` for the reason
 * `recipeFermentationStepChangesSchema` restates `pressurePsi`: `.partial()`
 * does not strip a `.default()`, so a patch that only moved the stand's length
 * would otherwise arrive carrying `whirlpoolEndTempC: null` and flatten the
 * brewer's cooling curve back to the model's own.
 */
export const recipeHopChangesSchema = recipeHopFieldsSchema
  .omit({ itemId: true })
  .extend({ whirlpoolEndTempC: z.number().finite().min(0).max(100).nullable() })
  .partial();

/**
 * Fields of a yeast an update may change.
 *
 * `starter` is restated without `.default(null)` for the reason
 * `recipeFermentationStepChangesSchema` restates `pressurePsi`: `.partial()`
 * does not strip a `.default()`, so a patch that only moves the pack count would
 * otherwise arrive carrying `starter: null` and throw the brewer's starter away.
 */
export const recipeYeastChangesSchema = recipeYeastFieldsSchema
  .omit({ itemId: true })
  .extend({ starter: RECIPE_YEAST_STARTER_FIELD })
  .partial();

/** Fields of a misc an update may change. @see recipeFermentableChangesSchema */
export const recipeMiscChangesSchema = recipeMiscFieldsSchema.omit({ itemId: true }).partial();

/** Add a fermentable, normally built by `snapshotFermentable`. */
export const recipeAddFermentablePayloadSchema = z.strictObject({
  ...recipeTarget,
  item: recipeFermentableSchema,
  index: insertIndexSchema,
});

/** Change fields of one fermentable. */
export const recipeUpdateFermentablePayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
  changes: recipeFermentableChangesSchema,
});

/** Remove one fermentable. */
export const recipeRemoveFermentablePayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
});

/** Reorder the fermentable list. The ids must be exactly the current set, permuted. */
export const recipeReorderFermentablesPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemIds: z.array(documentIdSchema).max(64),
});

/** Add a hop addition. */
export const recipeAddHopPayloadSchema = z.strictObject({
  ...recipeTarget,
  item: recipeHopSchema,
  index: insertIndexSchema,
});

/** Change fields of one hop addition — typically this lot's alpha acid. */
export const recipeUpdateHopPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
  changes: recipeHopChangesSchema,
});

/** Remove one hop addition. */
export const recipeRemoveHopPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
});

/** Reorder the hop list. */
export const recipeReorderHopsPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemIds: z.array(documentIdSchema).max(64),
});

/** Add a yeast pitch. */
export const recipeAddYeastPayloadSchema = z.strictObject({
  ...recipeTarget,
  item: recipeYeastSchema,
  index: insertIndexSchema,
});

/** Change fields of one yeast pitch. */
export const recipeUpdateYeastPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
  changes: recipeYeastChangesSchema,
});

/** Remove one yeast pitch. */
export const recipeRemoveYeastPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
});

/** Reorder the yeast list. */
export const recipeReorderYeastsPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemIds: z.array(documentIdSchema).max(16),
});

/** Add a miscellaneous addition. */
export const recipeAddMiscPayloadSchema = z.strictObject({
  ...recipeTarget,
  item: recipeMiscSchema,
  index: insertIndexSchema,
});

/** Change fields of one miscellaneous addition. */
export const recipeUpdateMiscPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
  changes: recipeMiscChangesSchema,
});

/** Remove one miscellaneous addition. */
export const recipeRemoveMiscPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemId: documentIdSchema,
});

/** Reorder the misc list. */
export const recipeReorderMiscsPayloadSchema = z.strictObject({
  ...recipeTarget,
  itemIds: z.array(documentIdSchema).max(64),
});

// ---------------------------------------------------------------------------
// Schedules
// ---------------------------------------------------------------------------

/**
 * Fields of a mash step an update may change.
 *
 * `decoction` is restated without `.default(null)` for the same reason
 * `endTempC` is below: the document schema fills a missing key so old recipes
 * still load, but an update that only touches `targetTempC` must not invent
 * `decoction: null` and wipe the brewer's pull spec.
 */
export const recipeMashStepChangesSchema = recipeMashStepSchema
  .omit({ stepId: true })
  .extend({ decoction: MASH_STEP_DECOCTION_FIELD })
  .partial();

/**
 * Fields of a fermentation step an update may change.
 *
 * `endTempC` is restated without `.default(null)`: the document schema fills a
 * missing key so old recipes still load, but an update that only touches `days`
 * must not invent `endTempC: null` and wipe a ramp. `pressurePsi` is restated
 * for the same reason, and would fail the same way: a patch moving a
 * temperature would otherwise arrive carrying `pressurePsi: null` and vent a
 * spunded step.
 */
export const recipeFermentationStepChangesSchema = recipeFermentationStepSchema
  .omit({ stepId: true })
  .extend({
    endTempC: z.number().finite().min(-10).max(60).nullable(),
    pressurePsi: FERMENTATION_STEP_PRESSURE_FIELD,
  })
  .partial();

/** Add a mash step. */
export const recipeAddMashStepPayloadSchema = z.strictObject({
  ...recipeTarget,
  step: recipeMashStepSchema,
  index: insertIndexSchema,
});

/** Change fields of one mash step. */
export const recipeUpdateMashStepPayloadSchema = z.strictObject({
  ...recipeTarget,
  stepId: documentIdSchema,
  changes: recipeMashStepChangesSchema,
});

/** Remove one mash step. */
export const recipeRemoveMashStepPayloadSchema = z.strictObject({
  ...recipeTarget,
  stepId: documentIdSchema,
});

/** Reorder the mash schedule. */
export const recipeReorderMashStepsPayloadSchema = z.strictObject({
  ...recipeTarget,
  stepIds: z.array(documentIdSchema).max(16),
});

/** Add a fermentation step. */
export const recipeAddFermentationStepPayloadSchema = z.strictObject({
  ...recipeTarget,
  step: recipeFermentationStepSchema,
  index: insertIndexSchema,
});

/** Change fields of one fermentation step. */
export const recipeUpdateFermentationStepPayloadSchema = z.strictObject({
  ...recipeTarget,
  stepId: documentIdSchema,
  changes: recipeFermentationStepChangesSchema,
});

/** Remove one fermentation step. */
export const recipeRemoveFermentationStepPayloadSchema = z.strictObject({
  ...recipeTarget,
  stepId: documentIdSchema,
});

/** Reorder the fermentation schedule. */
export const recipeReorderFermentationStepsPayloadSchema = z.strictObject({
  ...recipeTarget,
  stepIds: z.array(documentIdSchema).max(16),
});

// ---------------------------------------------------------------------------
// Water
// ---------------------------------------------------------------------------

/**
 * Set the source water.
 *
 * The one water command that *creates* the section: a recipe with
 * `water: null` gains a default section built around this profile. Every other
 * `water.*` command needs the section to exist already and fails with
 * `invalid-state` if it does not, because silently materialising a water
 * section from "add 2 g gypsum" would invent a source profile out of nothing.
 */
export const waterSetSourceProfilePayloadSchema = z.strictObject({
  ...recipeTarget,
  profile: waterProfileDocSchema,
});

/** Set the RO/distilled dilution. */
export const waterSetDilutionPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Share of the brewing water replaced, 0–1. */
  dilutionFraction: z.number().finite().min(0).max(1),
  /** What the dilution water is; `null` means ion-free RO. Unchanged when omitted. */
  dilutionProfile: waterProfileDocSchema.nullable().optional(),
});

/** Replace the salt additions wholesale — the shape the solver's output takes. */
export const waterSetSaltAdditionsPayloadSchema = z.strictObject({
  ...recipeTarget,
  saltAdditions: z.array(recipeSaltAdditionSchema).max(32),
});

/** Replace the acid additions wholesale. */
export const waterSetAcidAdditionsPayloadSchema = z.strictObject({
  ...recipeTarget,
  acidAdditions: z.array(recipeAcidAdditionSchema).max(32),
});

/** Set (or clear) the target water profile the achieved water is reported against. */
export const waterSetTargetProfilePayloadSchema = z.strictObject({
  ...recipeTarget,
  profile: waterProfileDocSchema.nullable(),
});

/**
 * Set (or clear) the water rule the section aims at instead of a profile.
 *
 * Its own command rather than a nullable second field on
 * `water.setTargetProfile`, even though the two are mutually exclusive: the
 * panel offers them as two ways of saying a target, and a single command
 * carrying both would make "aim at a rule" and "aim at a profile" the same
 * undo step with a payload nobody can read.
 */
export const waterSetTargetRulePayloadSchema = z.strictObject({
  ...recipeTarget,
  /** The two conditions to satisfy, or `null` to aim at nothing. */
  rule: waterRuleDocSchema.nullable(),
});

/** Set the target mash pH. */
export const waterSetTargetPhPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Target mash pH, pH units. */
  targetMashPh: z.number().finite().min(4).max(7),
});

/**
 * Set the kettle pH the brew day aims at, or clear it back to core's default.
 *
 * Its own command rather than a second field on `water.setTargetPh`, for the
 * reason `recipe.setPackaging` gives for the opposite choice: those two fields
 * are picked together on one panel, and these two are not. The mash target sits
 * beside a predicted mash pH; the kettle target is a note to brew day, and
 * bundling them would make undoing one silently rewrite the other.
 */
export const waterSetKettleTargetPhPayloadSchema = z.strictObject({
  ...recipeTarget,
  /** Target kettle pH, pH units, or `null` for core's default (5.1). */
  kettleTargetPh: z.number().finite().min(4).max(7).nullable(),
});

/** Drop the whole water section. */
export const waterClearPayloadSchema = z.strictObject({ ...recipeTarget });

// ---------------------------------------------------------------------------
// Equipment profiles
// ---------------------------------------------------------------------------

/**
 * Fields of an equipment profile an update may change. Identity and `builtin`
 * are not among them.
 *
 * Wrapped in the same legacy lift `equipmentProfileDocSchema` carries, because
 * the command log is replayed from the beginning and an `equipment.update`
 * written before `mashTunLossL` existed meant the old thing by
 * `mashTunDeadspaceL` — unrecoverable wort. The two cases are distinguishable
 * because the only writer of that key is the equipment form, which sends the
 * profile's whole field set: a `changes` carrying deadspace *and* loss is
 * current, one carrying deadspace alone predates the split.
 */
export const equipmentChangesSchema = z.preprocess(
  (raw) => {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
    const rec = raw as Record<string, unknown>;
    if (rec.mashTunLossL !== undefined || rec.mashTunDeadspaceL === undefined) return raw;
    return { ...rec, mashTunDeadspaceL: 0, mashTunLossL: rec.mashTunDeadspaceL };
  },
  equipmentProfileDocFieldsSchema
    .omit({ id: true, builtin: true, createdAt: true, updatedAt: true })
    .partial()
    // `.partial()` leaves a `.default()` in place, which would put these in every
    // patch that omits them and quietly overwrite what the brewer set. See
    // `DECOCTION_FIELDS`.
    .extend({
      mashStandingLossCPerHour: DECOCTION_FIELDS.mashStandingLossCPerHour.optional(),
      decoctionBoilOffLPerHour: DECOCTION_FIELDS.decoctionBoilOffLPerHour.optional(),
      decoctionPotVolumeL: DECOCTION_FIELDS.decoctionPotVolumeL.optional(),
      fermenterVolumeL: FERMENTER_VOLUME_FIELD.fermenterVolumeL.optional(),
      brewReminders: BREW_REMINDERS_FIELD.brewReminders.optional(),
    }),
);

/**
 * Create an equipment profile.
 *
 * `from` is the duplicate path — including "duplicate a built-in so I can edit
 * it", which is how a user escapes the read-only built-ins. Without it the new
 * profile starts from the shipped 5 gal all-grain numbers.
 */
export const equipmentCreatePayloadSchema = z.strictObject({
  /** Id for the new profile. Must not already exist. */
  profileId: documentIdSchema,
  /** Name for the new profile. */
  name: nameSchema,
  /** A profile to clone. Its id, `builtin` flag and timestamps are replaced. */
  from: equipmentProfileDocSchema.optional(),
});

/** Change fields of an equipment profile. Built-ins refuse. */
export const equipmentUpdatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  changes: equipmentChangesSchema,
});

/** Delete an equipment profile. Built-ins refuse; recipes pointing at it fall back to the default. */
export const equipmentDeletePayloadSchema = z.strictObject({ profileId: documentIdSchema });

/** Insert an equipment profile verbatim — the inverse of `equipment.delete`. */
export const equipmentRestorePayloadSchema = z.strictObject({
  profile: equipmentProfileDocSchema,
});

// ---------------------------------------------------------------------------
// Saved water profiles
// ---------------------------------------------------------------------------

/** Fields of a saved water profile an update may change. Identity and `builtin` are not among them. */
export const waterProfileChangesSchema = savedWaterProfileDocSchema
  .omit({ id: true, builtin: true, createdAt: true, updatedAt: true })
  .partial();

/**
 * Create a saved water profile.
 *
 * `from` is both the "type in my water report" path (the client builds the
 * document and passes it whole) and the "duplicate a built-in so I can edit it"
 * path, which is how a user escapes the read-only built-ins. Without it the new
 * profile starts from ion-free RO — zeros, which is the honest blank for a
 * water analysis, rather than someone else's tap.
 */
export const waterProfileCreatePayloadSchema = z.strictObject({
  /** Id for the new profile. Must not already exist. */
  profileId: documentIdSchema,
  /** Name for the new profile. */
  name: nameSchema,
  /** A profile to clone. Its id, `builtin` flag and timestamps are replaced. */
  from: savedWaterProfileDocSchema.optional(),
});

/** Change fields of a saved water profile. Built-ins refuse. */
export const waterProfileUpdatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  changes: waterProfileChangesSchema,
});

/**
 * Delete a saved water profile. Built-ins refuse.
 *
 * Recipes are unaffected: a recipe's water section holds a *copy* of the ions,
 * never a reference to a library id, so deleting the library entry cannot move
 * a recipe's mash pH. That is what keeps this a one-document change and
 * therefore exactly invertible.
 */
export const waterProfileDeletePayloadSchema = z.strictObject({ profileId: documentIdSchema });

/** Insert a saved water profile verbatim — the inverse of `waterProfile.delete`. */
export const waterProfileRestorePayloadSchema = z.strictObject({
  profile: savedWaterProfileDocSchema,
});

// ---------------------------------------------------------------------------
// Batches
// ---------------------------------------------------------------------------

/** Addresses a batch. Every `batch.*` payload extends this. */
const batchTarget = { batchId: documentIdSchema };

/**
 * Create a batch from a fully-built document.
 *
 * `from` is **required**, unlike `recipe.create`'s: there is no such thing as a
 * blank batch. A batch is defined by the two snapshots it freezes, and this
 * package can neither resolve a recipe id to a document (that is the caller's
 * `resolveEquipmentProfileDoc` + `state.recipes[…]` lookup, done at dispatch
 * time) nor invent a name containing today's date (no clock, no locale). The
 * caller builds the whole document with {@link buildBatchDoc} and hands it over.
 */
export const batchCreatePayloadSchema = z.strictObject({
  /** Id for the new batch. Must not already exist. */
  batchId: documentIdSchema,
  /** The complete batch document. Its `id`, `schemaVersion` and timestamps are replaced. */
  from: batchDocSchema,
  /** Name for the new batch; defaults to the template's. */
  name: nameSchema.optional(),
});

/**
 * Re-take a planned batch's frozen copies from the live recipe and equipment.
 *
 * The counterpart to {@link batchCreatePayloadSchema}, and required to carry
 * whole documents for exactly the same reason: this package cannot resolve a
 * recipe id to a document, nor work out which equipment profile that recipe
 * points at. The caller does both lookups and hands over the results.
 *
 * Both snapshots move together. `batch.create` freezes them as a pair, and a
 * batch holding today's recipe beside last month's copy of the equipment
 * profile that recipe references is a state nothing else in the app can
 * produce — so re-taking half would invent one.
 *
 * The reducer refuses this on any batch that has become a record; see
 * `batchResnapshotRefusal` for the four refusals and why each one exists.
 */
export const batchResnapshotPayloadSchema = z.strictObject({
  ...batchTarget,
  /** The live recipe. Its `id` must match the batch's back-link. */
  recipe: recipeDocSchema,
  /** The equipment profile that recipe currently resolves to. */
  equipment: equipmentProfileDocSchema,
});

/**
 * Insert a batch document verbatim, timestamps and all — an **upsert**.
 *
 * Same triple duty as `recipe.restore`: undo of `batch.delete`, the exact
 * inverse for any future batch command whose precise inverse would be lossy,
 * and the whole-document delta a full-state sync push carries (PLAN §8.3).
 */
export const batchRestorePayloadSchema = z.strictObject({
  /** The document to insert. Its own `id` is used. */
  batch: batchDocSchema,
});

/** Delete a batch. */
export const batchDeletePayloadSchema = z.strictObject({ ...batchTarget });

/** Rename a batch. */
export const batchRenamePayloadSchema = z.strictObject({ ...batchTarget, name: nameSchema });

/**
 * Move a batch to another status.
 *
 * Deliberately unconstrained: any status to any status. Brewing is not a state
 * machine — a brewer who tapped "packaged" a day early must be able to go back,
 * and a batch logged retrospectively starts at `packaged` and never passes
 * through `brew-day` at all. Guiding the *usual* order is the stepper's job in
 * the UI; refusing the unusual one here would only teach people to lie.
 */
export const batchSetStatusPayloadSchema = z.strictObject({
  ...batchTarget,
  status: batchStatusSchema,
  /**
   * The packaged day to write with the move, **YYYY-MM-DD** or `null`; left
   * alone when absent. The caller reads the clock, since the reducer reads none.
   * Unconstrained by `status` so the inverse can put back both fields at once.
   */
  packagedOn: isoDateSchema.nullable().optional(),
});

/** Set (or clear) the day the batch was packaged. */
export const batchSetPackagedOnPayloadSchema = z.strictObject({
  ...batchTarget,
  /** **YYYY-MM-DD**, or `null` for "not packaged yet". */
  packagedOn: isoDateSchema.nullable(),
});

/** Set (or clear) the instant the brew day happened. */
export const batchSetBrewedAtPayloadSchema = z.strictObject({
  ...batchTarget,
  /** **ms since epoch**, or `null` for "not brewed yet". */
  brewedAt: timestampSchema.nullable(),
});

/**
 * The measurement fields an update may change.
 *
 * ## Merge semantics — the one thing to get right here
 *
 * A key that is **present** is written, *including when its value is `null`*:
 * `null` is how a brewer clears a mistyped reading, and it has to be
 * distinguishable from "I didn't mention that field". A key that is **absent**
 * (or explicitly `undefined`, which is what a JSON round-trip turns an absent
 * key into and back) is left alone.
 *
 * That is the opposite convention from every other `changes`-shaped payload in
 * this file, where `undefined` and `null` both mean "skip" because the
 * underlying field is not nullable. It is spelled out rather than shared with
 * {@link mergeDefined} for exactly that reason — see `mergePresent` in
 * `commands/helpers.ts`.
 *
 * `preBoil` and `postBoil` merge **one level deep**: `{ preBoil: { sg: 1.042 } }`
 * sets the gravity and leaves the volume alone. The group itself cannot be set
 * to `null`; clear its two halves individually.
 *
 * `fermentationReadings` is absent on purpose. A list needs stable ids and
 * positional inverses, which is what
 * `batch.addFermentationReading` / `batch.removeFermentationReading` provide.
 */
export const batchMeasurementChangesSchema = z.strictObject({
  /** Into the kettle after the sparge; merged one level deep. */
  preBoil: batchGravityVolumeSchema.partial().optional(),
  /** Out of the kettle after the boil; merged one level deep. */
  postBoil: batchGravityVolumeSchema.partial().optional(),
  /** Original gravity as pitched. */
  originalGravity: specificGravitySchema.nullable().optional(),
  /** Wort into the fermenter, **litres**. */
  fermenterVolumeL: measuredVolumeSchema.nullable().optional(),
  /** Beer into keg/bottles, **litres**. */
  packagedVolumeL: measuredVolumeSchema.nullable().optional(),
  /** Final gravity at packaging. */
  finalGravity: specificGravitySchema.nullable().optional(),
  /**
   * The kettle's pH, before and after the dose; merged one level deep, exactly
   * as {@link preBoil} is.
   *
   * That is what lets the brew-day screen write the reading now, the acid five
   * minutes later and the after-adjustment reading five minutes after that
   * without any of the three wiping the other two. The group itself cannot be
   * set to `null`; clear its three fields individually.
   *
   * `batchKettleMeasurementSchema` carries **no defaults**, which is what makes
   * `.partial()` safe here — see `document/batch.ts`'s `migrateBatchMeasurements`.
   */
  kettle: batchKettleMeasurementSchema.partial().optional(),
});

/** Merge measurements onto a batch. See {@link batchMeasurementChangesSchema} for the semantics. */
export const batchUpdateMeasurementsPayloadSchema = z.strictObject({
  ...batchTarget,
  measurements: batchMeasurementChangesSchema,
});

/** Append (or insert) a fermentation gravity reading. */
export const batchAddFermentationReadingPayloadSchema = z.strictObject({
  ...batchTarget,
  reading: batchFermentationReadingSchema,
  index: insertIndexSchema,
});

/** Remove one fermentation gravity reading. */
export const batchRemoveFermentationReadingPayloadSchema = z.strictObject({
  ...batchTarget,
  readingId: documentIdSchema,
});

/** Replace the prose for one stage. */
export const batchSetStageNotesPayloadSchema = z.strictObject({
  ...batchTarget,
  /** Which stage's notes to write. Need not be the batch's current status. */
  stage: batchStatusSchema,
  notes: notesSchema,
});

/**
 * Tick or un-tick one brew-day step.
 *
 * `at` is the completion instant, not a boolean, so the brew sheet can print
 * "Mash in — 09:14" afterwards and so two devices ticking the same step
 * converge on a value rather than on `true`. `null` un-ticks.
 */
export const batchToggleStepPayloadSchema = z.strictObject({
  ...batchTarget,
  /** Timeline step id, generated from the batch's frozen snapshot. */
  stepId: documentIdSchema,
  /** Completion instant, **ms since epoch**, or `null` to un-tick. */
  at: timestampSchema.nullable(),
});

/**
 * Start, restart or clear one step's timer.
 *
 * Stores the **started-at anchor**, never a remaining duration. Remaining time
 * is `startedAt + duration − now`, computed at render, which is the only form
 * that survives a reload, a backgrounded tab, and a sync to the brewer's phone
 * in the garage. Restarting a running timer is just another `startedAt`.
 */
export const batchSetStepTimerPayloadSchema = z.strictObject({
  ...batchTarget,
  /** Timeline step id. */
  stepId: documentIdSchema,
  /** Start instant, **ms since epoch**, or `null` to clear the timer. */
  startedAt: timestampSchema.nullable(),
});

/**
 * Set (or clear) the instant this batch's ingredients left the cupboard.
 *
 * A separate setter rather than a flag folded into `batch.setStatus` or
 * `batch.setBrewedAt`, for the reason those two are separate from each other:
 * they record *different facts*. "I brewed this on Saturday" and "the grain for
 * it came out of stock" are independently true, independently undoable, and a
 * batch logged retrospectively has the first without ever having the second.
 * Folding them together would also make the idempotence guard impossible to
 * clear on undo without also rewinding the brew date.
 *
 * Shaped exactly like `batch.setBrewedAt` — an instant or `null` — so it
 * inverts the same way and needs no new pattern.
 */
export const batchSetInventoryDeductedPayloadSchema = z.strictObject({
  ...batchTarget,
  /** **ms since epoch**, or `null` for "not deducted". */
  at: timestampSchema.nullable(),
  /**
   * Frozen COGS at this deduct. Written when `at` is set; ignored (cleared)
   * when `at` is `null`. Optional so older clients that only sent `at` still
   * apply — they leave a deducted batch with no cost snapshot.
   */
  costSnapshot: batchCostSnapshotSchema.nullable().optional(),
});

/**
 * Set (or clear) the culture this batch was pitched with.
 *
 * Shaped like `batch.setInventoryDeducted` — a whole value or `null` — and
 * inverted the same way, with the prior record. A pitch is one fact recorded
 * once, so there is no partial patch: correcting the generation means restating
 * the pitch, which is also what the brewer means when they say it.
 *
 * The stock movement is **not** in here. Pitching a jar takes it out of the
 * cupboard, and that is an `inventory.adjustQuantity` the caller dispatches
 * alongside this one, exactly as a brew-day deduction pairs its adjustments
 * with `batch.setInventoryDeducted`. Two commands in one `dispatchBatch` is one
 * undo entry, and it keeps a pitch that was never in inventory expressible as
 * this command alone.
 */
export const batchSetPitchedYeastPayloadSchema = z.strictObject({
  ...batchTarget,
  /** The record, or `null` for "not recorded". */
  pitchedYeast: batchPitchedYeastSchema.nullable(),
});

/**
 * Size (or clear) the starter this batch is building from the jar it took.
 *
 * A patch of one key on the record rather than a whole-record write, because
 * the rest of the record is the jar's provenance and a resize must not be able
 * to restate it. `RECIPE_YEAST_STARTER_FIELD` is the recipe row's field without
 * its `.default(null)`, which is what keeps the two starters in exactly the
 * same shape without this payload inheriting a default it has no use for.
 *
 * The command refuses when no culture has been chosen and when the chosen one
 * is dry: core will not plan a starter for dry yeast, so neither will the
 * document.
 */
/**
 * Change how this batch is packaged, or hand a choice back to the plan
 * (PLAN-BATCH-PACKAGING D-BP2).
 *
 * Each key is optional, so an omitted one is left alone, and nullable, so an
 * explicit `null` means "as the recipe planned". At least one must be given:
 * a command that says nothing has nothing to undo either.
 */
export const batchSetPackagingPayloadSchema = z
  .strictObject({
    ...batchTarget,
    method: packagingMethodSchema.nullable().optional(),
    primingSugar: primingSugarSchema.nullable().optional(),
    carbonationVolumes: z.number().finite().min(0).max(6).nullable().optional(),
  })
  .refine(
    (payload) =>
      payload.method !== undefined ||
      payload.primingSugar !== undefined ||
      payload.carbonationVolumes !== undefined,
    { message: 'Give at least one of method, primingSugar or carbonationVolumes.' },
  );

export const batchSetYeastStarterPayloadSchema = z.strictObject({
  ...batchTarget,
  /** The starter, or `null` for "this jar goes straight in". */
  starter: RECIPE_YEAST_STARTER_FIELD,
});

/**
 * Stamp (or unstamp) the moment the chosen culture went into the wort.
 *
 * The second half of a take: `batch.setPitchedYeast` records which jar left the
 * fridge, and this records that it reached the fermenter. Separate because the
 * two moments are days apart whenever a starter is involved, and one command
 * that wrote both would make "taken, not pitched yet" unsayable.
 */
export const batchSetYeastPitchedAtPayloadSchema = z.strictObject({
  ...batchTarget,
  /** When it went in, **ms since epoch**, or `null` for "not yet". */
  pitchedAt: timestampSchema.nullable(),
});

/**
 * Append (or insert) a cellar task. Same list shape as a fermentation reading:
 * a whole item plus an optional index, invertible by id.
 */
export const batchAddCellarTaskPayloadSchema = z.strictObject({
  ...batchTarget,
  task: batchCellarTaskSchema,
  index: insertIndexSchema,
});

/** Patch one cellar task. Absent keys are left alone. */
export const batchUpdateCellarTaskPayloadSchema = z.strictObject({
  ...batchTarget,
  taskId: documentIdSchema,
  changes: batchCellarTaskSchema.omit({ taskId: true }).partial(),
});

/** Remove one cellar task. Invertible with its original position. */
export const batchRemoveCellarTaskPayloadSchema = z.strictObject({
  ...batchTarget,
  taskId: documentIdSchema,
});

// ---------------------------------------------------------------------------
// Tastings
// ---------------------------------------------------------------------------

/**
 * Append (or insert) a tasting note. The same list shape as a cellar task: a
 * whole item plus an optional index, invertible by id.
 */
export const batchAddTastingPayloadSchema = z.strictObject({
  ...batchTarget,
  tasting: batchTastingSchema,
  index: insertIndexSchema,
});

/**
 * Fields of a tasting an update may change.
 *
 * A plain `.partial()` of the document schema, and it is only safe as one
 * because **nothing in `batchTastingSchema` carries a `.default()`** — see the
 * note there. `.partial()` leaves a default in place, so a defaulted key would
 * arrive in every patch that omits it and overwrite what the brewer typed;
 * `recipeFermentationStepChangesSchema` above and `DECOCTION_FIELDS` are the two
 * places that already had to be repaired for it. If a tasting field ever gains a
 * default, restate it here as `.optional()` the way those two do.
 *
 * Merge semantics follow {@link batchMeasurementChangesSchema}, not the
 * `mergeDefined` convention the rest of this file uses: a **present** key is
 * written even when its value is `null`, because unscoring a section a brewer
 * scored by mistake has to be expressible and is not the same act as not
 * mentioning it. An absent key, or an explicit `undefined`, is left alone. See
 * `mergePresent` in `commands/helpers.ts`.
 */
export const batchTastingChangesSchema = batchTastingSchema.omit({ tastingId: true }).partial();

/** Patch one tasting. @see batchTastingChangesSchema */
export const batchUpdateTastingPayloadSchema = z.strictObject({
  ...batchTarget,
  tastingId: documentIdSchema,
  changes: batchTastingChangesSchema,
});

/** Remove one tasting. Invertible with its original position. */
export const batchRemoveTastingPayloadSchema = z.strictObject({
  ...batchTarget,
  tastingId: documentIdSchema,
});

/**
 * Record a photograph whose bytes are already in R2.
 *
 * Same list shape as a cellar task — a whole item plus an optional index — and
 * the same reason for it: the descriptor needs a stable id so the removal is
 * invertible by id rather than by position.
 *
 * The **caller uploads first**. `POST /api/batches/:batchId/photos` mints the
 * `photoId`, measures and sniffs the bytes, stores the object, and answers with
 * exactly the descriptor this payload carries. Ordering it the other way round
 * would put a document entry in front of an upload that may still fail, and the
 * grid would draw a tile with nothing behind it; support attachments make the
 * same choice for the same reason (`worker/support.ts`).
 */
export const batchAddPhotoPayloadSchema = z.strictObject({
  ...batchTarget,
  photo: batchPhotoSchema,
  index: insertIndexSchema,
});

/**
 * Write one photograph's caption.
 *
 * A setter rather than a `changes` patch, and that is the whole design: a
 * caption is the only part of a descriptor a brewer authors, and every other
 * field is a measurement the Worker took. A `.partial()` change set derived
 * from {@link batchPhotoSchema} would be a way to rewrite `bytes` and
 * `contentType` from the client, which nothing should be able to do.
 *
 * Shaped exactly like `batch.setStageNotes` — an address and a string — so it
 * inverts the same way and needs no new pattern.
 */
export const batchSetPhotoCaptionPayloadSchema = z.strictObject({
  ...batchTarget,
  photoId: documentIdSchema,
  caption: z.string().max(280),
});

/**
 * Take a photograph off a batch.
 *
 * **Not invertible**, alone among the list removals in this file, and the
 * command's own TSDoc argues it: the bytes go with it, so an inverse that put
 * the descriptor back would restore a tile pointing at an object R2 no longer
 * holds. See `definitions/batch.ts`.
 */
export const batchRemovePhotoPayloadSchema = z.strictObject({
  ...batchTarget,
  photoId: documentIdSchema,
});

// ---------------------------------------------------------------------------
// Blending sessions
// ---------------------------------------------------------------------------

/** Addresses one blending session. Every `blend.*` payload extends this. */
const blendTarget = { blendId: documentIdSchema };

/**
 * Fields of a blend an update may change. Identity and the timestamps are not
 * among them — those ride on the envelope, the way every other document does.
 *
 * Wrapped in the document's own legacy lift, the way `equipmentChangesSchema`
 * above is, and for the same reason: the command log is replayed from the
 * beginning, so a `blend.update` written when the key was `fruits` has to
 * still mean its rows (D-BS2).
 *
 * The lift is conditional, and that is the whole of it. A patch is a set of
 * keys the brewer touched, so running the lift on changes that mention no
 * addition would add `additions: []` to every rename and every carbonation
 * tweak, and blank the additions of the blend it was applied to. Only changes
 * that carry `fruits` or `fruit` are talking about additions at all.
 */
function liftBlendChanges(raw: unknown): unknown {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return raw;
  const rec = raw as Record<string, unknown>;
  if (!('fruits' in rec) && !('fruit' in rec)) return raw;
  return liftBlendAdditions(rec);
}

export const blendChangesSchema = z.preprocess(
  liftBlendChanges,
  blendDocFieldsSchema.omit({ id: true, createdAt: true, updatedAt: true }).partial(),
);

/**
 * Create a blending session.
 *
 * Without `from` the session starts empty — a name and the default target
 * carbonation, no beers yet — which is how a new blend is made: the beers are
 * pulled in afterwards. `from` is the duplicate path. It is not a verbatim
 * copy: the id, the timestamps, the stage and the stage dates are the new
 * session's own, for the reason `blendCreate` gives.
 */
export const blendCreatePayloadSchema = z.strictObject({
  /** Id for the new session. Must not already exist. */
  blendId: documentIdSchema,
  /** Name for the new session. */
  name: nameSchema,
  /** A document to clone. Its id, timestamps, stage and stage dates are replaced. */
  from: blendDocSchema.optional(),
});

/** Change fields of a blending session. A present `null` clears a nullable field. */
export const blendUpdatePayloadSchema = z.strictObject({
  ...blendTarget,
  changes: blendChangesSchema,
});

/**
 * Move a blending session to another stage, and say which day it got there.
 *
 * Unconstrained in direction, for `batch.setStatus`'s reason: a blend logged
 * retrospectively starts at `packaged` and a blender who tapped too early has
 * to be able to go back. Which way the stepper *suggests* is the page's
 * business.
 *
 * `on` is the day the stage was reached, and it is decided on the page rather
 * than read off a clock here (D-BS4, the rule `batch.setBrewedAt` follows), so
 * that replaying the log a year later stamps the same date it stamped the
 * first time. Omitting it leaves whatever date is stored, which is how "moved
 * back and forth" does not rewrite history. Only a dated stage may carry one:
 * `planned` is `createdAt` and `archived` is a drawer, and a date on either
 * would be a client bug with nowhere to land.
 */
export const blendSetStagePayloadSchema = z
  .strictObject({
    ...blendTarget,
    stage: blendStageSchema,
    /** The day this stage was reached, **YYYY-MM-DD**. */
    on: isoDateSchema.optional(),
  })
  .refine((payload) => payload.on === undefined || isBlendDatedStage(payload.stage), {
    message: 'Only blended, conditioning and packaged take a date.',
    path: ['on'],
  });

/** Delete a blending session. */
export const blendDeletePayloadSchema = z.strictObject({ ...blendTarget });

/**
 * Insert a blending session verbatim — an **upsert**, and the inverse of
 * `blend.delete`.
 */
export const blendRestorePayloadSchema = z.strictObject({
  blend: blendDocSchema,
});

/**
 * The blend's three cellar-task payloads (PLAN-UX-PASS E1).
 *
 * `batchAddCellarTaskPayloadSchema` and its two siblings with `blendId` in
 * place of `batchId`, over the same `batchCellarTaskSchema` the document
 * reuses — one schema, two documents, for the reason `blendDocFieldsSchema`
 * gives. The merge convention is `mergeDefined`, as the batch's is: an absent
 * key is a key this patch did not mention, and there is no field here a
 * blender can mean to clear.
 */
export const blendAddCellarTaskPayloadSchema = z.strictObject({
  ...blendTarget,
  task: batchCellarTaskSchema,
  index: insertIndexSchema,
});

/** Patch one of a blend's cellar tasks. Absent keys are left alone. */
export const blendUpdateCellarTaskPayloadSchema = z.strictObject({
  ...blendTarget,
  taskId: documentIdSchema,
  changes: batchCellarTaskSchema.omit({ taskId: true }).partial(),
});

/** Remove one of a blend's cellar tasks. Invertible with its original position. */
export const blendRemoveCellarTaskPayloadSchema = z.strictObject({
  ...blendTarget,
  taskId: documentIdSchema,
});

// ---------------------------------------------------------------------------
// Journals
// ---------------------------------------------------------------------------

/** Addresses one journal. Every `journal.*` payload extends this. */
const journalTarget = { journalId: documentIdSchema };

/**
 * Fields of a journal an update may change: its name and its tags. Nothing
 * else — the blocks have four commands of their own, because a whole-array
 * `changes` would make every keystroke a 200-block write and every undo a
 * 200-block restore.
 *
 * Written out rather than `.pick()`ed off the document, and that is the trap
 * `common.ts` describes at `ingredientLinkSchema` rather than a style
 * preference: `tags` carries a `.default([])` on the document so a journal
 * written before tags existed still parses, a `.default()` survives
 * `.partial()`, and a picked one would put `tags: []` on every patch that named
 * only the name — silently clearing the brewer's labels on every rename.
 *
 * `batchId` is D-M5's compatibility stamp. It is not a field of a journal any
 * more (the link lives on the block, D-M1), but command logs and outboxes hold
 * updates that name it, and a replay that refused them would fail on a
 * document's own history. It is accepted, applied as the stamping the
 * document's migration does, and sent by nothing.
 */
export const journalChangesSchema = z.strictObject({
  name: nameSchema.optional(),
  tags: tagsSchema.optional(),
  /** @deprecated The compatibility stamp of D-M5. Nothing sends this. */
  batchId: documentIdSchema.nullable().optional(),
});

/**
 * Create a journal.
 *
 * Without `from` it starts empty — a name, no tags, no blocks. `from` is both
 * the duplicate path, as it is for a blend, and how a journal started from a
 * batch or a blend page opens with a block already naming it (D-M2).
 */
export const journalCreatePayloadSchema = z.strictObject({
  /** Id for the new journal. Must not already exist. */
  journalId: documentIdSchema,
  /** Name for the new journal. */
  name: nameSchema,
  /** A document to clone. Its id and timestamps are replaced. */
  from: journalDocSchema.optional(),
});

/** Change a journal's name or its tags. @see journalChangesSchema */
export const journalUpdatePayloadSchema = z.strictObject({
  ...journalTarget,
  changes: journalChangesSchema,
});

/** Delete a journal. */
export const journalDeletePayloadSchema = z.strictObject({ ...journalTarget });

/** Insert a journal verbatim — an **upsert**, and the inverse of `journal.delete`. */
export const journalRestorePayloadSchema = z.strictObject({
  journal: journalDocSchema,
});

/** Add one block to the stack, at `index` or appended. */
export const journalAddBlockPayloadSchema = z.strictObject({
  ...journalTarget,
  block: journalBlockSchema,
  /** Where it lands. Absent appends; out of range is clamped. */
  index: z.number().int().min(0).optional(),
});

/**
 * Replace one block verbatim, matched on `block.blockId`.
 *
 * One command for every kind of edit a block takes — the body of a note, the
 * date it happened, whether it is public — because a block is small enough to
 * send whole and because a per-field `changes` schema for a six-member union is
 * six schemas nobody would keep in step.
 */
export const journalSetBlockPayloadSchema = z.strictObject({
  ...journalTarget,
  block: journalBlockSchema,
});

/** Move one block to `index`. Out of range is clamped. */
export const journalMoveBlockPayloadSchema = z.strictObject({
  ...journalTarget,
  blockId: documentIdSchema,
  index: z.number().int().min(0),
});

/** Take one block off the stack. */
export const journalRemoveBlockPayloadSchema = z.strictObject({
  ...journalTarget,
  blockId: documentIdSchema,
});

// ---------------------------------------------------------------------------
// Inventory
// ---------------------------------------------------------------------------

/** Addresses one stocked item. Every `inventory.*` payload extends this. */
const inventoryTarget = { itemId: documentIdSchema };

/**
 * Add a lot to the cupboard, from a fully-built document.
 *
 * `from` is required, as `batch.create`'s is and for the same reason: an
 * inventory item is defined by what it is — a catalog reference, a snapshotted
 * display name, a per-lot alpha acid — and this package can neither resolve a
 * catalog id nor read the clock that stamps the lot. The caller builds the
 * document with {@link buildInventoryItemDoc}; this command's jobs are to
 * refuse a duplicate id and to re-stamp identity, schema version and both
 * timestamps from the envelope, exactly as the other `create`s do.
 */
export const inventoryAddPayloadSchema = z.strictObject({
  /** Id for the new item. Must not already exist. */
  itemId: documentIdSchema,
  /** The complete item. Its `id`, `schemaVersion` and timestamps are replaced. */
  from: inventoryItemDocSchema,
  /** Name for the new item; defaults to the template's. */
  name: nameSchema.optional(),
});

/**
 * Fields of an inventory item an update may change.
 *
 * Identity, schema version and the timestamps are not among them. `quantity`
 * **is**: typing a corrected number into the row is an edit, and it is a
 * different operation from `inventory.adjustQuantity`, which records a movement
 * of stock. Both exist because "I miscounted, it is 400 g" and "I used 100 g"
 * are different facts with different histories.
 *
 * Derived from the Fields schema rather than the refined document because Zod
 * refuses .omit() on a refinement; the one-provenance rule is re-checked by
 * the update reducer on the merged lot.
 */
export const inventoryChangesSchema = inventoryItemFieldsSchema
  .omit({ id: true, schemaVersion: true, createdAt: true, updatedAt: true })
  .partial()
  .extend({
    // Don't inherit the document's `.default(null)` — an omitted change must
    // stay omitted, not become "clear the price". Same for the harvest record:
    // `.partial()` leaves a `.default()` in place, so without this an
    // `inventory.update` touching only the notes would carry `yeastHarvest:
    // null` and quietly un-harvest the jar. It is the rule `DECOCTION_FIELDS`
    // states at length, and every nullable-with-a-default field on the document
    // has to be restated here. The three dates (PLAN-HOP-AGE §A) are the newest
    // to need it, and would otherwise have cleared a lot's purchase date every
    // time a brewer edited its notes.
    unitCost: z.number().finite().nonnegative().max(10_000).nullable().optional(),
    yeastHarvest: yeastHarvestSchema.nullable().optional(),
    purchasedAt: isoDateSchema.nullable().optional(),
    cropYear: cropYearSchema.nullable().optional(),
    hopStorage: z
      .strictObject({ place: hopStoragePlaceSchema, packaging: hopPackagingSchema })
      .nullable()
      .optional(),
  });

/** Change fields of one stocked item. A present `null` clears a nullable field. */
export const inventoryUpdatePayloadSchema = z.strictObject({
  ...inventoryTarget,
  changes: inventoryChangesSchema,
});

/** Remove a lot from the cupboard. */
export const inventoryRemovePayloadSchema = z.strictObject({ ...inventoryTarget });

/**
 * Insert an inventory item verbatim, timestamps and all — an **upsert**.
 *
 * The same triple duty `recipe.restore` and `batch.restore` carry: undo of
 * `inventory.remove`, the exact inverse wherever a precise one would be lossy,
 * and the whole-document delta a full-state sync push uses.
 */
export const inventoryRestorePayloadSchema = z.strictObject({
  /** The document to insert. Its own `id` is used. */
  item: inventoryItemDocSchema,
});

/**
 * Move stock: add or subtract from what is held.
 *
 * ## Why a delta and not a new value
 *
 * A deduction is a *movement*, and two devices (or a brew-day deduction and a
 * shopping trip) that both write an absolute quantity would silently lose one
 * of the two movements. A delta composes, inverts trivially, and reads as what
 * happened.
 *
 * ## No clamping
 *
 * The reducer does **not** clamp at zero. An adjustment that would take a lot
 * below zero is refused with `invalid-state`, because clamping would make the
 * inverse (`−delta`) restore the wrong number and quietly break undo. Callers
 * that mean "use up whatever is left" compute that delta themselves — which is
 * exactly what `planInventoryDeduction` does for a partially-stocked
 * ingredient.
 *
 * Both the delta and the resulting quantity are rounded onto the storage grid
 * ({@link roundQuantity}) so that `q + d − d` returns to `q` exactly.
 */
export const inventoryAdjustQuantityPayloadSchema = z.strictObject({
  ...inventoryTarget,
  /**
   * How much to add, in the item's own unit (**grams**, or **packs** for
   * yeast). Negative takes stock out. Zero is legal and is a no-op.
   */
  delta: z.number().finite().min(-10_000_000).max(10_000_000),
  /**
   * Why, in a few words — `Brewed "Fixture Pale Ale"`, `Restocked`.
   *
   * Not stored on the document: the command log *is* the history, and a field
   * on the item would only ever show the most recent reason. It rides here so
   * the undo label, the sync log and a future stock-movement report all say the
   * same thing.
   */
  reason: z.string().min(1).max(200).nullable().optional(),
});

// ---------------------------------------------------------------------------
// Custom ingredients
// ---------------------------------------------------------------------------

/** Addresses one library entry. Every `customIngredient.*` payload extends this. */
const customIngredientTarget = { ingredientId: documentIdSchema };

/**
 * Fields of a custom ingredient an update may change.
 *
 * Identity, schema version, timestamps and `kind` are not among them — changing
 * kind would silently drop the other kind's numbers. The reducer re-validates
 * the merged document, so a hop update that names a fermentable-only field is
 * refused rather than producing a document the schema would reject.
 */
export const customIngredientChangesSchema = z.strictObject({
  name: nameSchema.optional(),
  notes: notesSchema.optional(),
  maker: z.string().min(1).max(60).nullable().optional(),
  class: z
    .enum(['grain', 'steeping-grain', 'dry-extract', 'liquid-extract', 'sugar', 'non-fermentable'])
    .optional(),
  maltClass: z
    .enum(['base', 'munich-vienna', 'wheat-other', 'crystal', 'roasted', 'acidulated', 'none'])
    .optional(),
  extractFineGrindDryBasisPercent: z.number().finite().min(0).max(100).optional(),
  moisturePercent: z.number().finite().min(0).max(100).optional(),
  colorLovibond: z.number().finite().min(0).max(700).optional(),
  diastaticPowerLintner: z.number().finite().min(0).max(250).optional(),
  distilledWaterPh: z.number().finite().min(3).max(7).nullable().optional(),
  bufferingCapacityMEqPerKgPerPh: z.number().finite().min(-800).max(800).nullable().optional(),
  acidContentPercentLactic: z.number().finite().min(0).max(100).nullable().optional(),
  origin: z.string().min(1).max(60).nullable().optional(),
  purpose: z.enum(['bittering', 'aroma', 'dual']).optional(),
  alphaAcidPercent: z.number().finite().min(0).max(30).optional(),
  betaAcidPercent: z.number().finite().min(0).max(20).nullable().optional(),
  totalOilMlPer100g: z.number().finite().min(0).max(6).nullable().optional(),
  lab: z.string().min(1).max(60).nullable().optional(),
  attenuationMinPercent: z.number().finite().min(0).max(100).optional(),
  attenuationMaxPercent: z.number().finite().min(0).max(100).optional(),
  temperatureMinC: z.number().finite().min(-10).max(60).nullable().optional(),
  temperatureMaxC: z.number().finite().min(-10).max(60).nullable().optional(),
  type: z
    .enum([
      'ale',
      'lager',
      'wheat',
      'wine',
      'brett',
      'other',
      'water-agent',
      'acid',
      'alkali',
      'fining',
      'nutrient',
      'water-treatment',
      'enzyme',
      'spice',
      'hop-flavour',
    ])
    .optional(),
  form: z.enum(['dry', 'liquid', 'powder', 'granular', 'tablet', 'solid']).optional(),
  use: z
    .enum(['water', 'mash', 'sparge', 'boil', 'whirlpool', 'primary', 'secondary', 'packaging'])
    .optional(),
  timingMinutes: z.number().finite().min(0).max(20160).nullable().optional(),
  concentrationPercent: z.number().finite().min(0.1).max(100).nullable().optional(),
});

/**
 * Add a custom ingredient from a fully-built document.
 *
 * `from` is required, as `inventory.add`'s is: the entry *is* its spec, and
 * this package has no clock. The caller builds the document with
 * {@link buildCustomIngredientDoc}; this command re-stamps identity, schema
 * version and both timestamps from the envelope.
 */
export const customIngredientAddPayloadSchema = z.strictObject({
  /** Id for the new entry. Must not already exist. */
  ingredientId: documentIdSchema,
  /** The complete document. Its `id`, `schemaVersion` and timestamps are replaced. */
  from: customIngredientDocSchema,
  /** Name for the new entry; defaults to the template's. */
  name: nameSchema.optional(),
});

/** Change fields of one library entry. A present `null` clears a nullable field. */
export const customIngredientUpdatePayloadSchema = z.strictObject({
  ...customIngredientTarget,
  changes: customIngredientChangesSchema,
});

/**
 * Remove a library entry.
 *
 * Refused with `in-use` while a lot or a live recipe row still points at it
 * (`ingredientId`), so a link can never dangle by deletion. Recipes that
 * snapshotted it are otherwise untouched — their numbers are copies.
 *
 * ## One command, not a batch
 *
 * The cascade rides on this payload rather than being a batch of
 * `inventory.remove`s in front of it, because a batch is atomic only on the
 * device that built it: the sync engine announces each command separately and
 * the Durable Object applies them one at a time, continuing past a rejection.
 * A concurrent edit could land every lot removal and refuse the entry removal
 * behind them, leaving the brewer with the ingredient they tried to delete and
 * none of the stock they had. Evaluated as one command, that cannot happen.
 */
/** The most lots one cascading remove can take — the `lots` array's ceiling. */
export const CASCADE_LOT_LIMIT = 256;

export const customIngredientRemovePayloadSchema = z.strictObject({
  ...customIngredientTarget,
  /**
   * What the removal may take with it. `none` — the default when omitted —
   * refuses while anything references the entry. `empty-lots` removes every
   * linked lot holding no stock and then the entry; a linked lot with stock,
   * or any recipe row, still refuses. There is no variant that deletes stock,
   * and none that edits a recipe a brewer may have brewed from.
   */
  cascade: z.enum(['none', 'empty-lots']).optional(),
});

/**
 * Insert a custom ingredient verbatim — an upsert, and the inverse of
 * `customIngredient.remove`.
 */
export const customIngredientRestorePayloadSchema = z.strictObject({
  ingredient: customIngredientDocSchema,
  /**
   * Lots to put back beside the entry, verbatim — the empty lots a cascade
   * took. The inverse of `customIngredient.remove { cascade: 'empty-lots' }`
   * carries them here so one undo restores the entry and its lots together,
   * which is what keeps `apply(invert(s, c), apply(s, c)) === s` exact.
   *
   * The ceiling is {@link CASCADE_LOT_LIMIT}, and the reducer refuses a removal
   * that would exceed it rather than letting one succeed whose undo could not
   * be expressed — the two sides read the same constant so they cannot drift.
   */
  lots: z.array(inventoryItemDocSchema).max(CASCADE_LOT_LIMIT).optional(),
});

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

/**
 * Change workspace settings.
 *
 * Only the keys present are touched. `unitSystem` and `units` interact:
 * naming a system installs its preset, individual unit changes are applied on
 * top, and `unitSystem` is then recomputed from the result (so picking `us` and
 * then switching temperature back to Celsius leaves the workspace on `mixed`,
 * which is the honest answer).
 */
export const settingsUpdatePayloadSchema = z.strictObject({
  /** Install a named system's preset before applying `units`. */
  unitSystem: unitSystemSchema.optional(),
  /** Per-kind overrides. */
  units: unitPreferencesSchema.partial().optional(),
  /** Decimal pounds vs lb+oz for grain mass. Does not change `unitSystem`. */
  massFormat: massFormatSchema.optional(),
  /** Where hops are kept by default, for the ageing curve. @see PLAN-HOP-AGE §E */
  hopStorageDefault: z
    .strictObject({ place: hopStoragePlaceSchema, packaging: hopPackagingSchema })
    .optional(),
  /** Equipment profile new recipes adopt; `null` falls back to the first built-in. */
  defaultEquipmentProfileId: documentIdSchema.nullable().optional(),
  /** Water profile "Set up water" seeds from; `null` restores built-in RO. */
  defaultWaterProfileId: documentIdSchema.nullable().optional(),
  /** Batch volume new recipes start at, **litres**. */
  defaultBatchVolumeL: positiveSchema.max(10_000).optional(),
  /** Boil length new recipes start at, **minutes**. */
  defaultBoilTimeMinutes: nonNegativeSchema.max(1440).optional(),
  /**
   * ISO 4217 code lot prices are shown in. Changing it does not convert
   * numbers already stored on lots.
   */
  currency: currencyCodeSchema.optional(),
  /**
   * Salts the water solver may suggest — the brewer's cupboard. Replaced
   * wholesale, never merged, and never empty: the checkbox that would empty it
   * is the checkbox the settings page refuses to uncheck.
   */
  solverSalts: z.array(saltIdSchema).min(1).optional(),
  stockedAcids: z.array(acidChoiceIdSchema).min(1).optional(),
  /**
   * When brew day warns, and how insistently. @see PLAN-BREW-ALERTS D-BA3
   *
   * A partial, because the two Settings rows are two controls: pressing "every
   * 15 seconds" must not also send whatever lead the page read a minute ago.
   * The reducer merges the pair key by key.
   */
  brewAlerts: brewAlertsSchema.partial().optional(),
  /** Days before brew day the calendar puts **Starter** on, one to five. */
  starterLeadDays: starterLeadDaysSchema.optional(),
});

// ---------------------------------------------------------------------------
// Schedule profiles (PLAN-PROFILES P1)
// ---------------------------------------------------------------------------

/**
 * Create a saved schedule, blank or from a supplied one.
 *
 * `from` is the duplicate path, and it is the only way to get an editable copy
 * of a built-in — the same escape hatch `equipment.create` provides, for the
 * same reason: the shipped set is product data and must not be editable in
 * place, but "start from Full body and warm it two degrees" is the commonest
 * thing a brewer wants to do with it.
 */
export const mashProfileCreatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  name: nameSchema,
  /** A profile to clone. Its id, `builtin` flag and timestamps are replaced. */
  from: mashProfileDocSchema.optional(),
});

/** Change a saved mash schedule. Built-ins refuse. */
export const mashProfileUpdatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  changes: mashProfileChangesSchema,
});

/** Delete a saved mash schedule. Built-ins refuse. */
export const mashProfileDeletePayloadSchema = z.strictObject({ profileId: documentIdSchema });

/** Insert one verbatim — the inverse of a delete. */
export const mashProfileRestorePayloadSchema = z.strictObject({ profile: mashProfileDocSchema });

/** @see mashProfileCreatePayloadSchema */
export const fermentationProfileCreatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  name: nameSchema,
  from: fermentationProfileDocSchema.optional(),
});

/** Change a saved fermentation schedule. Built-ins refuse. */
export const fermentationProfileUpdatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  changes: fermentationProfileChangesSchema,
});

/** Delete a saved fermentation schedule. Built-ins refuse. */
export const fermentationProfileDeletePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
});

/** Insert one verbatim — the inverse of a delete. */
export const fermentationProfileRestorePayloadSchema = z.strictObject({
  profile: fermentationProfileDocSchema,
});

// ---------------------------------------------------------------------------
// Additions profiles (PLAN-ADDITIONS-PROFILES)
// ---------------------------------------------------------------------------

/**
 * Create a saved additions profile, blank or from a supplied one.
 *
 * Saving from a recipe is this followed by a `miscProfile.update` carrying the
 * rows, the batch volume and the style, in one `dispatchAll`.
 */
export const miscProfileCreatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  name: nameSchema,
  /** A profile to clone. Its id, `builtin` flag and timestamps are replaced. */
  from: miscProfileDocSchema.optional(),
});

/** Change a saved additions profile. */
export const miscProfileUpdatePayloadSchema = z.strictObject({
  profileId: documentIdSchema,
  changes: miscProfileChangesSchema,
});

/** Delete a saved additions profile. */
export const miscProfileDeletePayloadSchema = z.strictObject({ profileId: documentIdSchema });

/** Insert one verbatim — the inverse of a delete. */
export const miscProfileRestorePayloadSchema = z.strictObject({ profile: miscProfileDocSchema });
